import * as React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ActivityMapLegend.module.scss';

type CylinderProps = {
  radius: number;
  tilt: number;
  height: number;
  topClassName?: string;
  baseClassName?: string;
} & React.SVGProps<SVGGElement>;

const SvgCylinder: React.FunctionComponent<CylinderProps> = ({
  radius,
  tilt,
  height,
  topClassName,
  baseClassName,
  ...other
}) => {
  const diameter = radius * 2;
  const rx = radius;
  const ry = tilt * radius;

  // separate into two paths so we can color them differently
  const dTop = `
        M 0,${ry}
        a ${rx},${ry} 0,0,0 ${diameter} 0
        a ${rx},${ry} 0,0,0 ${-diameter} 0`;

  const dBase = `
        M 0,${ry}
        l 0,${height}
        a ${rx},${ry} 0,0,0 ${diameter} 0
        l 0,${-height}`;

  return (
    <g {...other}>
      <path d={dBase} className={baseClassName} />
      <path d={dTop} className={topClassName} />
    </g>
  );
};

type ArrowProps = {
  length: number;
  arrowHeadWidth?: number;
  arrowHeadHeight?: number;
} & React.SVGProps<SVGPathElement>;

const SvgVerticalArrow: React.FunctionComponent<ArrowProps> = ({
  length,
  arrowHeadWidth = 4,
  arrowHeadHeight = arrowHeadWidth * 1.8,
  ...other
}) => {
  const d = `
    M 0,${length}
    l 0,${-length}
    m ${-arrowHeadWidth},${arrowHeadHeight / 2}
    l ${arrowHeadWidth},${-arrowHeadHeight / 2}
    l ${arrowHeadWidth},${arrowHeadHeight / 2}
    `;
  return <path d={d} {...other} />;
};

const ActivityMapLegend: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const width = 105;
  const height = 110;

  // size settings
  const radius = 10;
  const tilt = 0.4;
  const cylinderMargin = 6;
  const cylinderMaxHeight = 30;
  const cylinderHeights = [
    cylinderMaxHeight * 0.22,
    cylinderMaxHeight * 0.55,
    cylinderMaxHeight,
  ];

  const cylindersOuterWidth = 2 * (2 * radius + cylinderMargin) + 2 * radius;
  const sizeGroupHeight = cylinderMaxHeight + 20 + 10;

  // color settings
  const barWidth = width * 0.4;
  const barHeight = 6;

  return (
    <svg width={width} height={height} data-testid="activity-map-legend">
      <g transform="translate(0, 4)">
        <g>
          <SvgVerticalArrow
            className={styles.arrow}
            length={cylinderMaxHeight + 10}
            transform={`translate(95, 0)`}
          />
          <g
            transform={`translate(${
              (width - cylindersOuterWidth) / 2 - 4
            } , 1)`}
          >
            <SvgCylinder
              height={cylinderHeights[0]}
              radius={radius}
              tilt={tilt}
              topClassName={styles.cylinderTop}
              baseClassName={styles.cylinderBase}
              transform={`translate(0, ${
                cylinderMaxHeight - cylinderHeights[0]
              })`}
            />
            <SvgCylinder
              height={cylinderHeights[1]}
              radius={radius}
              tilt={tilt}
              topClassName={styles.cylinderTop}
              baseClassName={styles.cylinderBase}
              transform={`translate(${2 * radius + cylinderMargin}, ${
                cylinderMaxHeight - cylinderHeights[1]
              })`}
            />
            <SvgCylinder
              height={cylinderHeights[2]}
              radius={radius}
              tilt={tilt}
              topClassName={styles.cylinderTop}
              baseClassName={styles.cylinderBase}
              transform={`translate(${2 * (2 * radius + cylinderMargin)}, 0)`}
            />
          </g>
          <g transform={`translate(0, ${cylinderMaxHeight + 10})`}>
            <text
              className={styles.label}
              textAnchor="middle"
              x={width / 2}
              dy={20}
            >
              {t('collection.map_legend_1')}
            </text>
          </g>
        </g>
        <g
          transform={`translate(${(width - 2 * barWidth - 2) / 2}, ${
            sizeGroupHeight + 20
          })`}
        >
          <g>
            <rect
              width={barWidth}
              height={barHeight}
              className={styles.barOlder}
            />
            <text className={styles.label} dy={20}>
              {t('collection.map_legend_2')}
            </text>
          </g>
          <g transform={`translate(${barWidth + 2}, 0)`}>
            <rect
              width={barWidth}
              height={barHeight}
              className={styles.barNewer}
            />
            <text className={styles.label} dy={20}>
              {t('collection.map_legend_3')}
            </text>
          </g>
        </g>
      </g>
    </svg>
  );
};

export default ActivityMapLegend;
